"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const material_1 = require("@mui/material");
const axios_1 = require("axios");
const pino_1 = __importDefault(require("pino"));
const react_1 = require("react");
const zod_1 = require("zod");
const strings_1 = require("../../../../../../../constants/strings");
const sessionQuery_1 = require("../../../../../../../queries/sessionQuery");
const routerUtils_1 = require("../../../../../../../router/routerUtils");
const phoneNumberUtils_1 = require("../../../../../../../utils/phoneNumberUtils");
const zustandStore_1 = require("../../../../../../../zustandStore");
const formWrapper_1 = __importDefault(require("../../../formWrapper"));
const useValidation_1 = require("../../../hooks/useValidation");
const logger = (0, pino_1.default)();
const validationSchema = zod_1.z.object({
    code1: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code2: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code3: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code4: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code5: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
    code6: zod_1.z
        .string()
        .length(1, 'Single digit')
        .refine(val => /^\d$/.test(val), 'Must be a digit'),
});
const StyledTextArea = (0, material_1.styled)(material_1.TextareaAutosize)(({ theme }) => ({
    width: '40px !important',
    height: '48px !important',
    marginRight: `${theme.tsGoUnit.tsUnitHalf} !important`,
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px 1px !important',
    lineHeight: `${theme.tsGoUnit.tsUnit} !important`,
    background: '#FFFFFF',
    borderRadius: '2px',
    flex: 'none',
    resize: 'none',
    outline: '1px solid #CDD5DF',
    border: '4px solid transparent',
    boxSizing: 'border-box',
    fontWeight: theme.typography.tsFontWeight.bold,
    fontSize: `${theme.typography.pxToRem(38)} !important`,
    ...(globalThis.cardProvider.IS_HSBC_PROVIDER_SETUP
        ? {
            borderRadius: 0,
        }
        : {}),
    '&:last-child': {
        marginRight: '0',
    },
    '&:hover': {
        outline: '1px solid #A70262',
        border: '4px solid rgba(167, 2, 98, 0.3)',
    },
}));
const isIncompleteActivationError = (error) => {
    if (!(0, axios_1.isAxiosError)(error)) {
        return false;
    }
    return error.response?.status === 403 && error.response?.data?.errorCode === 'INCOMPLETE_ACTIVATION';
};
const isLockedAccountError = (error) => {
    if (!(0, axios_1.isAxiosError)(error)) {
        return false;
    }
    return error.response?.status === 403 && error.response?.data?.errorCode === 'LOCKED_ACCOUNT';
};
const EnterValidationCode = ({ challengeName }) => {
    const [formState, setFormState] = (0, react_1.useState)({
        code1: '',
        code2: '',
        code3: '',
        code4: '',
        code5: '',
        code6: '',
    });
    const { validateField, clearFieldError, validateAllSchema } = (0, useValidation_1.useValidation)();
    const { steps, setPageLoading, setCurrentStep } = (0, zustandStore_1.useZustand)(state => ({ ...state }));
    const sendSmsCode = (0, sessionQuery_1.useSendSmsCodeQuery)();
    const sendSmsMfaCode = (0, sessionQuery_1.useSendMfaCodeChallengeQuery)();
    const { navigateToLockedAccountMessage } = (0, routerUtils_1.useSigninMessageNavigation)();
    const fullPhoneNumber = steps.data.phoneSetup?.phoneDetails?.fullPhoneNumber || steps.data.mfaSms?.phoneDetails?.fullPhoneNumber;
    const maskedPhoneNumber = (0, phoneNumberUtils_1.maskPhoneNumber)(steps.data.phoneSetup?.phoneDetails?.phoneCountryCode || steps.data.mfaSms?.phoneDetails?.phoneCountryCode, steps.data.phoneSetup?.phoneDetails?.phoneNumber || steps.data.mfaSms?.phoneDetails?.phoneNumber);
    const [errorMessage, setErrorMessage] = (0, react_1.useState)(null);
    const inputRefs = (0, react_1.useRef)([]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // restrict to numbers only
        if (!/^\d*$/.test(value)) {
            return;
        }
        if (value.length === 6) {
            const updatedState = { ...formState };
            const codeDigits = [...value];
            codeDigits.forEach((digit, index) => {
                updatedState[`code${index + 1}`] = digit;
            });
            setFormState(updatedState);
            clearFieldError(name);
            inputRefs.current[5]?.focus();
        }
        else if (value.length === 1) {
            setFormState(prev => {
                const updatedState = { ...prev, [name]: value };
                clearFieldError(name);
                return updatedState;
            });
            const nextIndex = Number(name.replace('code', '')) + 1;
            if (nextIndex <= 6) {
                inputRefs.current[nextIndex - 1]?.focus();
            }
        }
    };
    const handleKeyDown = (e) => {
        if (!e) {
            return;
        }
        const { name = '' } = e.target;
        if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            const currentIndex = Number(name.replace('code', ''));
            const updatedState = { ...formState, [name]: '' };
            setFormState(updatedState);
            clearFieldError(name);
            if ((e.key === 'Backspace' || e.key === 'Delete') && currentIndex > 1) {
                inputRefs.current[currentIndex - 2]?.focus();
            }
        }
    };
    const handleBlur = (e) => {
        if (e) {
            const { name = '' } = e.target;
            validateField(validationSchema, name, formState);
        }
    };
    const handleMutationError = (error) => {
        logger.error({ error: (0, axios_1.isAxiosError)(error) ? error.response?.data : error });
        if (isIncompleteActivationError(error)) {
            logger.warn('Account activation is incomplete', {
                errorCode: (0, axios_1.isAxiosError)(error) ? error.response?.data?.errorCode : undefined,
                status: (0, axios_1.isAxiosError)(error) ? error.response?.status : undefined,
            });
            navigateToLockedAccountMessage('accountNotCreated');
            return;
        }
        if (isLockedAccountError(error)) {
            logger.warn('Account is locked', {
                errorCode: (0, axios_1.isAxiosError)(error) ? error.response?.data?.errorCode : undefined,
            });
            navigateToLockedAccountMessage('mfa');
            return;
        }
        setErrorMessage("You've entered the wrong code, please try again.");
    };
    const handleMutationSettled = () => {
        setPageLoading(false);
    };
    const clearFormState = () => {
        setFormState({
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
        });
    };
    const handleMutationSuccess = (stepIncrement) => {
        setCurrentStep(steps.currentStep + stepIncrement);
        clearFormState();
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        validateAllSchema(validationSchema, formState);
        setErrorMessage(null);
        const code = `${formState.code1}${formState.code2}${formState.code3}${formState.code4}${formState.code5}${formState.code6}`;
        const mutationConfig = {
            onError: handleMutationError,
            onSettled: handleMutationSettled,
        };
        if (challengeName === 'SMS_MFA') {
            sendSmsMfaCode.mutate({ code, challengeName }, {
                ...mutationConfig,
                onSuccess: () => handleMutationSuccess(1),
                onError: (error) => handleMutationError(error),
            });
        }
        else {
            sendSmsCode.mutate({ code, challengeName }, {
                ...mutationConfig,
                onSuccess: async (data) => {
                    if (data.phoneSetup.status === 'verified') {
                        handleMutationSuccess(1);
                    }
                },
                onError: (error) => handleMutationError(error),
            });
        }
    };
    return ((0, jsx_runtime_1.jsxs)(formWrapper_1.default, { formName: "Enter validation code", onSubmit: handleSubmit, submitButton: {
            label: strings_1.sessionStrings.CONTINUE,
            isDisabled: !validationSchema.safeParse(formState).success,
            style: { marginTop: 0 },
        }, isFormPending: sendSmsMfaCode.isPending || sendSmsCode.isPending, children: [fullPhoneNumber ? ((0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body1", style: {
                    fontStyle: 'normal',
                    lineHeight: '18px',
                    color: '#000000',
                }, children: `Enter the 6-digit code that was sent to ${maskedPhoneNumber}` })) : null, (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { display: 'flex', justifyContent: 'center', padding: '30px 0 10px' }, children: ['code1', 'code2', 'code3', 'code4', 'code5', 'code6'].map((code, index) => ((0, jsx_runtime_1.jsx)(StyledTextArea, { autoFocus: index === 0, name: code, value: formState[code], onChange: handleInputChange, onBlur: handleBlur, onKeyDown: handleKeyDown, maxLength: 1, minRows: 1, maxRows: 1, ref: el => (inputRefs.current[index] = el) }, code))) }), errorMessage && ((0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "body2", sx: {
                    color: theme => theme.palette.error.main,
                    textAlign: 'center',
                    mt: 1,
                    mb: 2,
                }, children: errorMessage })), (0, jsx_runtime_1.jsxs)(material_1.Typography, { variant: "body1", style: {
                    fontStyle: 'normal',
                    lineHeight: '18px',
                    color: '#000000',
                    textAlign: 'center',
                    paddingBottom: '30px',
                }, children: ["It may take a while for the code to arrive. ", (0, jsx_runtime_1.jsx)("br", {}), challengeName === 'SMS_MFA' ? 'OTP is only valid for 3 minutes.' : null] })] }));
};
exports.default = EnterValidationCode;
